import { useState, type FormEvent } from 'react'
import type { RouterOutputs } from 'shared/api'
import { trpcClient } from 'shared/api'
import { PrintHubLabel, type HubLabelData } from 'shared/components/hub-label'
import { Button } from 'shared/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from 'shared/components/ui/dialog'
import { Input } from 'shared/components/ui/input'
import { useTranslate } from 'shared/lib/i18n'

/**
 * «Этикетка не читается» on the batch's checklist (spec §22 Ф6, ticket 48 §4): the
 * worker finds the Parcel in hand inside THIS batch — by a part of its code, its
 * Recipient's name or its order — picks its row and prints TezGo's label (`TG…`);
 * then scans that code into the batch as usual. The hub has no network search
 * (ADR-0029): only this batch's lines are searched, by the server.
 *
 * The calls are injected (`find`, `loadLabel`, `print`) as the stubbed tRPC seam in
 * tests.
 */

export type BatchParcelRow = RouterOutputs['hubReceiving']['FindInBatch'][number]

export interface UnreadableLabelSeams {
	find?: (manifestId: string, query: string) => Promise<BatchParcelRow[]>
	loadLabel?: (parcelId: string) => Promise<HubLabelData>
	print?: () => void
}

const findViaTrpc = (manifestId: string, query: string) =>
	trpcClient.hubReceiving.FindInBatch.query({ manifestId, query })

export function UnreadableLabel({
	manifestId,
	find = findViaTrpc,
	loadLabel,
	print,
	onClosed,
}: {
	manifestId: string
	/** The dialog closed: the scan field takes the cursor back for the printed code. */
	onClosed?: () => void
} & UnreadableLabelSeams) {
	const { t } = useTranslate()
	const [open, setOpen] = useState(false)
	const [query, setQuery] = useState('')
	const [rows, setRows] = useState<BatchParcelRow[] | null>(null)
	const [tooShort, setTooShort] = useState(false)
	const [busy, setBusy] = useState(false)
	const [picked, setPicked] = useState<string | null>(null)

	const search = async (event: FormEvent) => {
		event.preventDefault()
		setPicked(null)
		if (query.trim().length < 2) {
			setTooShort(true)
			setRows(null)
			return
		}
		setTooShort(false)
		setBusy(true)
		try {
			setRows(await find(manifestId, query.trim()))
		} finally {
			setBusy(false)
		}
	}

	return (
		<Dialog
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (!next) onClosed?.()
			}}
		>
			<DialogTrigger asChild>
				<Button type='button' variant='outline' size='lg' data-testid='unreadable-open'>
					{t('hubLabel.unreadable')}
				</Button>
			</DialogTrigger>
			<DialogContent
				className='sm:max-w-xl'
				data-testid='unreadable'
				onCloseAutoFocus={(event) => event.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle>{t('hubLabel.unreadable')}</DialogTitle>
					<DialogDescription>{t('hubLabel.unreadableHint')}</DialogDescription>
				</DialogHeader>
				<form className='flex gap-2' onSubmit={search}>
					<Input
						autoFocus
						data-testid='unreadable-query'
						aria-label={t('hubLabel.query')}
						placeholder={t('hubLabel.query')}
						value={query}
						onChange={(e) => setQuery(e.target.value)}
					/>
					<Button type='submit' disabled={busy} data-testid='unreadable-find'>
						{t('hubLabel.find')}
					</Button>
				</form>
				{tooShort && <p className='text-sm text-destructive'>{t('hubLabel.queryTooShort')}</p>}
				{rows !== null && rows.length === 0 && (
					<p className='py-4 text-center text-sm text-muted-foreground' data-testid='unreadable-nothing'>
						{t('hubLabel.nothing')}
					</p>
				)}
				{rows !== null && rows.length > 0 && (
					<div className='flex max-h-[50vh] flex-col divide-y overflow-auto rounded-md border'>
						{rows.map((row) => (
							<div key={row.parcelId} className='flex flex-col gap-3 px-3 py-2.5' data-testid='unreadable-row'>
								<div className='flex items-center justify-between gap-3'>
									<div className='flex min-w-0 flex-col text-sm'>
										<span className='font-mono font-medium'>{row.trackingCode}</span>
										<span className='text-xs text-muted-foreground'>
											{row.recipientName} · {row.partnerOrderRef} · {row.point.id} ·{' '}
											{t(`hubLabel.lineState_${row.lineState}`)}
										</span>
									</div>
									{picked !== row.parcelId && (
										<Button type='button' size='sm' variant='outline' onClick={() => setPicked(row.parcelId)}>
											{t('hubLabel.pick')}
										</Button>
									)}
								</div>
								{picked === row.parcelId && (
									<PrintHubLabel
										parcelId={row.parcelId}
										{...(loadLabel ? { loadLabel } : {})}
										{...(print ? { print } : {})}
									/>
								)}
							</div>
						))}
					</div>
				)}
			</DialogContent>
		</Dialog>
	)
}
